import { useState } from 'react';
import { Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button3D } from '@/components/ui/button-3d';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface DuplicateDocumentButtonProps {
  /** Cria a cópia e devolve o id dela (para navegar). */
  onDuplicate: () => Promise<string | null>;
  /** O que muda na cópia — mostrado no diálogo ANTES de criar. */
  description: string;
  label?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * "Duplicar" com confirmação.
 *
 * A confirmação não é cerimônia: duplicar CRIA um documento (com número de
 * sequência próprio, que não volta atrás) e cada tipo descarta coisas
 * diferentes — assinaturas, cancelamento, agendamento, vínculo de origem. O
 * diálogo diz o que a cópia NÃO herda antes de criar, em vez de deixar o
 * usuário descobrir depois abrindo o documento.
 */
export function DuplicateDocumentButton({
  onDuplicate,
  description,
  label = 'Duplicar',
  className,
  disabled,
}: DuplicateDocumentButtonProps) {
  const [open, setOpen] = useState(false);
  const [working, setWorking] = useState(false);

  const handleConfirm = async () => {
    setWorking(true);
    try {
      const id = await onDuplicate();
      if (id) setOpen(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Não foi possível duplicar o documento.',
      );
    } finally {
      setWorking(false);
    }
  };

  return (
    <>
      <Button3D
        type="button"
        color="gray"
        className={className}
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        <Copy className="mr-2 h-4 w-4" />
        {label}
      </Button3D>

      <AlertDialog open={open} onOpenChange={(v) => !working && setOpen(v)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Duplicar documento?</AlertDialogTitle>
            <AlertDialogDescription>{description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {/* Cancelar NUNCA é desabilitado junto com o confirmar: com a
                mutation pendurada, os dois travados prendem o usuário no modal
                sem saída. Fechar no meio é seguro — a navegação da cópia só
                acontece depois que ela existe. */}
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); void handleConfirm(); }} disabled={working}>
              {working ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {working ? 'Duplicando...' : 'Duplicar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
